import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Input, Select, Table } from '../components/ui';
import { EmptyStateBlock, ErrorState, LoadingState } from '../components/states';
import { useToast } from '../app/ToastProvider';
import { useSession } from '../app/session';
import { ApiError } from '../api/client';
import {
  exportConnectors,
  listConnectorConfigs,
  listConnectors,
  optIn,
  optOut,
  removeConnectorConfig,
  saveConnectorConfig,
  type ConnectorConfigRow,
  type ConnectorEntry
} from '../api/connectors';

/** Human label for a licence class (D0). */
const LICENCE_LABEL: Record<string, string> = {
  commercial_ok: 'Commercial use',
  byo_key_only: 'Bring your own key',
  platform_brokered: 'Platform-brokered'
};

type Draft = { open: boolean; secret: string; params: string; error: string };

/**
 * X3.6 — Integrations (D0 dual-path).
 *
 * Per data type the tenant chooses a provider, brings their own key (write-once,
 * server-side) and/or opts in to the platform-brokered paid plan. The D0 rules
 * this screen must honour:
 *  - price, terms, licence class, attribution and retention are shown **before**
 *    consent, from the registry — never baked into the UI;
 *  - a **Path-1 fallback is always visible** (no lock-in): opting out keeps the
 *    tenant's own config;
 *  - links never show anywhere; a stored key is reported as `configured` only.
 */
export function Integrations() {
  const { session } = useSession();
  const { notify, notifyError } = useToast();
  const writable = (session?.role ?? 'viewer') !== 'viewer';

  const [connectors, setConnectors] = useState<ConnectorEntry[]>([]);
  const [configs, setConfigs] = useState<ConnectorConfigRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dataType, setDataType] = useState('');
  const [provider, setProvider] = useState('');
  const [draft, setDraft] = useState<Draft>({ open: false, secret: '', params: '{}', error: '' });
  const [busy, setBusy] = useState('');
  const [exportJson, setExportJson] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const [registry, rows] = await Promise.all([listConnectors(), listConnectorConfigs()]);
      setConnectors(registry);
      setConfigs(rows);
      setError('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load connectors.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const dataTypes = useMemo(() => [...new Set(connectors.map((entry) => entry.data_type))].sort(), [connectors]);
  const providers = useMemo(
    () => connectors.filter((entry) => !dataType || entry.data_type === dataType),
    [connectors, dataType]
  );

  const selected = providers.find((entry) => entry.id === provider) ?? null;
  const selectedConfig = selected ? configs.find((row) => row.connector_id === selected.id) ?? null : null;

  function chooseProvider(nextProvider: string) {
    setProvider(nextProvider);
    const entry = connectors.find((candidate) => candidate.id === nextProvider);
    const row = entry ? configs.find((candidate) => candidate.connector_id === entry.id) : null;
    setDraft({
      open: Boolean(entry),
      secret: '',
      params: JSON.stringify(row?.params ?? {}, null, 2),
      error: ''
    });
  }

  async function save() {
    if (!selected) return;
    let params: Record<string, unknown> = {};
    if (draft.params.trim()) {
      try {
        const parsed: unknown = JSON.parse(draft.params);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('not an object');
        params = parsed as Record<string, unknown>;
      } catch {
        setDraft((current) => ({ ...current, error: 'Params must be a JSON object.' }));
        return;
      }
    }
    const path = selectedConfig?.path === 'brokered' ? 'brokered' : 'byo';
    setBusy('save');
    try {
      await saveConnectorConfig({
        connector_id: selected.id,
        path,
        params,
        ...(draft.secret ? { secret: draft.secret } : {})
      });
      notify(`Saved ${selected.provider_name}`);
      // The key is write-once: drop it from the form the moment it is stored.
      setDraft({ open: true, secret: '', params: JSON.stringify(params, null, 2), error: '' });
      await load();
    } catch (cause) {
      setDraft((current) => ({ ...current, error: cause instanceof ApiError ? cause.message : 'Could not save.' }));
    } finally {
      setBusy('');
    }
  }

  async function toggleSubscription(entry: ConnectorEntry, active: boolean) {
    setBusy(entry.id);
    try {
      if (active) await optOut(entry.id);
      else await optIn(entry.id);
      notify(active ? `Opted out of ${entry.provider_name}` : `Opted in to ${entry.provider_name}`);
      await load();
    } catch (cause) {
      notifyError(cause instanceof ApiError ? cause.message : 'Could not change the subscription.');
    } finally {
      setBusy('');
    }
  }

  async function remove(entry: ConnectorEntry) {
    setBusy(entry.id);
    try {
      await removeConnectorConfig(entry.id);
      notify(`Removed ${entry.provider_name} config`);
      if (provider === entry.id) setDraft({ open: false, secret: '', params: '{}', error: '' });
      await load();
    } catch (cause) {
      notifyError(cause instanceof ApiError ? cause.message : 'Could not remove.');
    } finally {
      setBusy('');
    }
  }

  async function doExport() {
    try {
      const payload = await exportConnectors();
      const text = JSON.stringify(payload, null, 2);
      setExportJson(text);
      setCopied(false);
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
      } catch {
        // Clipboard unavailable (insecure context) — the textarea is the fallback.
      }
    } catch (cause) {
      notifyError(cause instanceof ApiError ? cause.message : 'Could not export.');
    }
  }

  return (
    <main className="workspace-main">
      <header className="page-header">
        <div>
          <span className="index-label">CONTROL ROOM / INTEGRATIONS</span>
          <h1>Integrations</h1>
        </div>
      </header>

      <p className="detail-note">
        Bring your own provider key, or opt in to a platform-brokered plan. Nothing is locked in:
        your config always falls back to Path&nbsp;1, and an export never contains your keys.
      </p>

      {loading && <LoadingState label="Loading connectors" />}
      {error && <ErrorState title="Integrations unavailable" detail={error} onRetry={() => void load()} />}

      {!loading && !error && connectors.length === 0 && (
        <EmptyStateBlock title="No connectors" detail="No provider is registered for this deployment." />
      )}

      {!loading && !error && connectors.length > 0 && (
        <section className="panel">
          <header className="panel-heading">
            <span className="index-label">PROVIDERS</span>
            <Select aria-label="Data type" value={dataType} onChange={(event) => { setDataType(event.target.value); setProvider(''); setDraft({ open: false, secret: '', params: '{}', error: '' }); }}>
              <option value="">All data types</option>
              {dataTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </Select>
          </header>
          <div className="panel-body">
            <label className="index-label" htmlFor="provider-select">PROVIDER</label>
            <Select id="provider-select" value={provider} onChange={(event) => chooseProvider(event.target.value)}>
              <option value="">Choose a provider</option>
              {providers.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.provider_name} — {entry.data_type}
                </option>
              ))}
            </Select>

            {selected && (
              <div className="connector-detail">
                <div className="connector-meta">
                  <Badge>{LICENCE_LABEL[selected.licence_class] ?? selected.licence_class}</Badge>
                  {selectedConfig?.configured ? <Badge>Configured</Badge> : <Badge>Not configured</Badge>}
                  <span className="detail-note">refresh ≥ {selected.refresh_interval_s}s</span>
                </div>

                {selected.attribution.required && (
                  <p className="detail-note">
                    Attribution required: {selected.attribution.text}
                    {selected.attribution.url ? ` (${selected.attribution.url})` : ''}
                  </p>
                )}

                {/* ---- Path 1: bring your own key (always available) ---- */}
                {selected.byo_supported && (
                  <div className="connector-path">
                    <span className="index-label">PATH 1 — BRING YOUR OWN KEY</span>
                    <label className="index-label" htmlFor="connector-key">API KEY (WRITE-ONCE)</label>
                    <Input
                      id="connector-key"
                      type="password"
                      autoComplete="off"
                      placeholder={selectedConfig?.configured ? 'Stored — enter a new key to replace it' : 'Paste your key'}
                      value={draft.secret}
                      disabled={!writable}
                      onChange={(event) => setDraft((current) => ({ ...current, secret: event.target.value }))}
                    />
                    <p className="detail-note">Stored server-side and never shown again. Replacing it invalidates the cached fetch.</p>
                  </div>
                )}

                <label className="index-label" htmlFor="connector-params">PARAMS (JSON — LOCATION, FEEDS, ETC.)</label>
                <textarea
                  id="connector-params"
                  className="control textarea"
                  rows={5}
                  value={draft.params}
                  disabled={!writable}
                  onChange={(event) => setDraft((current) => ({ ...current, params: event.target.value }))}
                />
                <p className="detail-note">Params contain no secrets, so they export cleanly.</p>

                {draft.error && (
                  <p className="dialog-alert" role="alert"><span aria-hidden="true">▲</span>{draft.error}</p>
                )}

                <div className="settings-actions">
                  <Button type="button" disabled={!writable || busy === 'save'} onClick={() => void save()}>
                    {busy === 'save' ? 'Saving…' : 'Save'}
                  </Button>
                  {selectedConfig && (
                    <Button variant="quiet" type="button" disabled={!writable || busy === selected.id} onClick={() => void remove(selected)}>
                      Remove
                    </Button>
                  )}
                </div>

                {/* ---- Path 2: platform-brokered paid plan ---- */}
                {selected.brokered && selected.brokered_plan ? (
                  <div className="connector-path">
                    <span className="index-label">PATH 2 — PLATFORM-BROKERED PLAN</span>
                    {/* Terms shown BEFORE consent (D0). */}
                    <ul className="connector-terms">
                      <li><span>Price</span><strong>{selected.brokered_plan.price_label}</strong></li>
                      <li><span>Billed per</span><strong>{selected.brokered_plan.metered_per}</strong></li>
                      <li><span>Data retention</span><strong>{selected.brokered_plan.retention_days} days</strong></li>
                      <li><span>Licence</span><strong>{LICENCE_LABEL[selected.licence_class] ?? selected.licence_class}</strong></li>
                      <li>
                        <span>Terms</span>
                        <a href={selected.brokered_plan.terms_url} target="_blank" rel="noreferrer noopener">Open terms</a>
                      </li>
                    </ul>
                    <div className="settings-actions">
                      {selectedConfig?.path === 'brokered' ? (
                        <Button variant="quiet" type="button" disabled={!writable || busy === selected.id} onClick={() => void toggleSubscription(selected, true)}>
                          Opt out (return to Path 1)
                        </Button>
                      ) : (
                        <Button type="button" disabled={!writable || busy === selected.id} onClick={() => void toggleSubscription(selected, false)}>
                          Opt in to the brokered plan
                        </Button>
                      )}
                    </div>
                    <p className="detail-note">
                      Opting out keeps your own config — brokered data is never the only copy of anything you can source yourself.
                    </p>
                  </div>
                ) : (
                  <p className="detail-note">No brokered plan for this provider — Path 1 only.</p>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {!loading && !error && (
        <section className="panel">
          <header className="panel-heading">
            <span className="index-label">CONFIGURED / {String(configs.length).padStart(2, '0')}</span>
            <Button variant="quiet" type="button" onClick={() => void doExport()}>Export (no secrets)</Button>
          </header>
          {configs.length === 0 ? (
            <div className="panel-body">
              <EmptyStateBlock title="Nothing configured" detail="Choose a provider above to add your first integration." />
            </div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Connector</th>
                  <th>Path</th>
                  <th>Configured</th>
                </tr>
              </thead>
              <tbody>
                {configs.map((row) => {
                  const entry = connectors.find((candidate) => candidate.id === row.connector_id);
                  return (
                    <tr key={row.connector_id}>
                      <td>{entry?.provider_name ?? row.connector_id}</td>
                      <td>{row.path === 'brokered' ? 'Platform-brokered' : 'Bring your own key'}</td>
                      <td>{row.configured ? 'Yes' : 'Not yet'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
          {exportJson && (
            <div className="panel-body">
              <label className="index-label" htmlFor="connector-export">EXPORT — SECRETS STRIPPED{copied ? ' (COPIED)' : ''}</label>
              <textarea id="connector-export" className="control textarea" rows={8} readOnly value={exportJson} />
            </div>
          )}
        </section>
      )}
    </main>
  );
}
